import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const weekDayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const parseTaskDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  if (dateStr.includes('-')) {
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) return { year, month, day };
    }
  }

  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      if (parts[2].length === 4) {
        const year = Number(parts[2]);
        const month = Number(parts[1]);
        const day = Number(parts[0]);
        if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) return { year, month, day };
      }
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) return { year, month, day };
    }
  }

  return null;
};

const toDateKey = ({ year, month, day }) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const formatDateLabel = (dateObj) => {
  const dayName = weekDayNames[dateObj.getDay()];
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${dayName}, ${day}/${month}/${year}`;
};

const CalendarBoard = ({ tasks, statusColors, onOpenNotes, getBrazilDate, onCreateTask, groups, allMembers = [] }) => {
  const [currentDate, setCurrentDate] = useState(getBrazilDate ? getBrazilDate() : new Date());
  const [viewMode, setViewMode] = useState('list');

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handlePrev = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const handleNext = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const handleToday = () => setCurrentDate(getBrazilDate ? getBrazilDate() : new Date());

  const handleDayClick = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (onCreateTask) onCreateTask(dateStr);
  };

  const tasksByMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;

    return tasks
      .map((task) => {
        const parsed = parseTaskDate(task.entrega);
        if (!parsed) return null;
        if (parsed.year !== year || parsed.month !== month) return null;
        return {
          task,
          parsed,
          dateKey: toDateKey(parsed),
          dateObj: new Date(parsed.year, parsed.month - 1, parsed.day),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const dateDiff = a.dateObj.getTime() - b.dateObj.getTime();
        if (dateDiff !== 0) return dateDiff;
        return (a.task?.elemento || '').localeCompare(b.task?.elemento || '', 'pt-BR');
      });
  }, [tasks, currentDate]);

  const groupedListByDate = useMemo(() => {
    const map = new Map();

    tasksByMonth.forEach((item) => {
      if (!map.has(item.dateKey)) {
        map.set(item.dateKey, {
          dateKey: item.dateKey,
          dateObj: item.dateObj,
          tasks: [],
        });
      }
      map.get(item.dateKey).tasks.push(item.task);
    });

    return Array.from(map.values()).sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  }, [tasksByMonth]);

  const groupsById = useMemo(() => {
    return (groups || []).reduce((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {});
  }, [groups]);

  const membersByInitials = useMemo(() => {
    return allMembers.reduce((acc, member) => {
      acc[member.initials] = member;
      return acc;
    }, {});
  }, [allMembers]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(month, year);
  const firstDay = getFirstDayOfMonth(month, year);
  const today = getBrazilDate ? getBrazilDate() : new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();

  const cells = [];

  for (let i = 0; i < firstDay; i += 1) {
    cells.push({ type: 'blank', key: `blank-${i}` });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const dayTasks = tasksByMonth.filter((item) => item.parsed.day === day).map((item) => item.task);
    const isToday = isCurrentMonth && todayDate === day;
    cells.push({ type: 'day', key: `day-${day}`, day, tasks: dayTasks, isToday });
  }

  return (
    <div className="flex flex-col h-full bg-[#181b26] border border-slate-700 rounded-md overflow-hidden animate-in fade-in">
      <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50 shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={handleToday} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-sm transition-colors font-medium">Hoje</button>
          <div className="flex items-center gap-2 text-slate-200">
            <button onClick={handlePrev} className="p-1.5 hover:bg-slate-700 rounded transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={handleNext} className="p-1.5 hover:bg-slate-700 rounded transition-colors"><ChevronRight size={18} /></button>
          </div>
          <h3 className="text-lg font-semibold text-white">{monthNames[month]} {year}</h3>
        </div>
        <div className="flex items-center bg-slate-800 rounded p-1 border border-slate-700 shadow-sm">
          <button onClick={() => setViewMode('month')} className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${viewMode === 'month' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:bg-slate-700'}`}>Mês</button>
          <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${viewMode === 'list' ? 'bg-blue-600 text-white shadow' : 'text-slate-300 hover:bg-slate-700'}`}>Lista</button>
        </div>
      </div>

      {viewMode === 'month' ? (
        <>
          <div className="grid grid-cols-7 border-b border-slate-700 bg-slate-800/30 shrink-0">
            {daysOfWeek.map(day => <div key={day} className="py-2 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider border-r border-slate-700/50 last:border-0">{day}</div>)}
          </div>

          <div className="flex-1 grid grid-cols-7 auto-rows-fr overflow-y-auto bg-[#11131c]">
            {cells.map(cell => {
              if (cell.type === 'blank') {
                return <div key={cell.key} className="border-r border-b border-slate-700/50 bg-slate-800/10"></div>;
              }
              return (
                <div key={cell.key} onClick={() => handleDayClick(cell.day)} className={`border-r border-b border-slate-700/50 p-1 flex flex-col transition-colors group/cell cursor-pointer ${cell.isToday ? 'bg-blue-900/15' : 'hover:bg-slate-800/30'}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="opacity-0 group-hover/cell:opacity-100 p-0.5 text-blue-400 transition-all" title="Criar tarefa neste dia">
                      <Plus size={12} />
                    </span>
                    <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${cell.isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400'}`}>{cell.day}</span>
                  </div>
                  <div className="flex-1 flex flex-col gap-0.5 overflow-y-auto custom-scrollbar">
                    {cell.tasks.slice(0, 5).map(task => {
                      const statusClass = statusColors[task.status] || 'bg-slate-700 text-white';
                      const bgClass = statusClass.split(' ')[0] || 'bg-slate-700';
                      return (
                        <div key={task.id} onClick={(e) => { e.stopPropagation(); onOpenNotes(task.id); }} className={`${bgClass} text-white text-[10px] font-medium px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity shadow-sm border border-black/20`} title={task.elemento}>
                          {task.elemento}
                        </div>
                      );
                    })}
                    {cell.tasks.length > 5 && (
                      <div className="text-[10px] text-slate-500 text-center">+{cell.tasks.length - 5} mais</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto bg-[#11131c] p-4 pb-16">
          {groupedListByDate.length > 0 ? (
            <div className="space-y-4">
              {groupedListByDate.map((dateGroup) => (
                <div key={dateGroup.dateKey} className="border border-slate-700 rounded-lg overflow-hidden bg-[#181b26]/70">
                  <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50 flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-slate-100">{formatDateLabel(dateGroup.dateObj)}</div>
                    <div className="text-xs text-slate-400">{dateGroup.tasks.length} tarefa{dateGroup.tasks.length !== 1 ? 's' : ''}</div>
                  </div>

                  <div className="divide-y divide-slate-800">
                    {dateGroup.tasks.map((task) => {
                      const statusClass = statusColors[task.status] || 'bg-slate-700 text-white';
                      const statusBg = statusClass.split(' ')[0] || 'bg-slate-700';
                      const group = groupsById[task.groupId];

                      return (
                        <div key={task.id} onClick={() => onOpenNotes(task.id)} className="px-4 py-3 hover:bg-slate-800/50 cursor-pointer transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusBg}`}></span>
                                <span className="text-sm text-slate-100 font-medium truncate">{task.elemento}</span>
                              </div>
                              <div className="mt-1 text-xs text-slate-400 flex items-center gap-2">
                                <span>{task.status || 'Sem status'}</span>
                                <span className="text-slate-600">•</span>
                                <span>{group?.title || 'Sem grupo'}</span>
                              </div>
                            </div>

                            {(task.equipe || []).length > 0 ? (
                              <div className="flex -space-x-1.5 shrink-0">
                                {(task.equipe || []).map((initials, index) => {
                                  const member = membersByInitials[initials];
                                  return (
                                    <div key={`${task.id}-${initials}-${index}`} className={`w-7 h-7 rounded-full ${member?.color || 'bg-slate-500'} flex items-center justify-center text-[10px] font-bold text-white border-2 border-[#11131c]`} title={member?.name || initials}>
                                      {initials}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-500 shrink-0">Sem equipe</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center px-6">
              <p className="text-slate-300 font-medium">Nenhuma tarefa com data neste mês.</p>
              <p className="text-slate-500 text-sm mt-1">Use o modo Mês para criar tarefas clicando em um dia.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarBoard;
